import { setAppConfig, getAppConfigValue } from '@elliemae/pui-app-sdk';
import { getDefaultTheme } from '@elliemae/pui-theme';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux'; // Import Provider
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import '../webroot/components/themes/vanilla/styles/base.css';
import { startRefreshTokenProcess } from '@frontend/auth';
import appConfig from './app.config.json';
import App from './route/index';
import Session, {
  IS_DEBUG,
  OPAQUE_TOKEN,
  PRICING_ID,
  REFRESH_TOKEN,
  USER_TYPE,
} from './common/services/Session';
import { PATH_DEBUG } from './route/constants';
import { configureStore } from './store'; // Import your configured Redux store

setAppConfig(appConfig);

const isDebugPath =
  typeof window !== 'undefined' &&
  window.location.pathname.startsWith(PATH_DEBUG);

// Tech Ops debug deep-link. Legacy deep-links into
// /debug?token=<opaqueToken>&refreshToken=<rt>&pricingId=<id>. We flag debug
// mode (login bypassed), store the opaque token + pricingId, and keep the token
// fresh via @frontend/auth — mirroring apps/ppe deep-link. initTechOpsData then
// resolves the pricingId to loan data with that opaque token (EPPS-100502).
if (isDebugPath) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const refreshToken = params.get('refreshToken');
  const pricingId = params.get('pricingId');

  Session.set(USER_TYPE, { userType: 'StandAlone' });
  Session.set(IS_DEBUG, true);
  if (pricingId) Session.set(PRICING_ID, pricingId);
  if (token) Session.set(OPAQUE_TOKEN, token);
  if (refreshToken) {
    Session.set(REFRESH_TOKEN, refreshToken);
  }

  // Strip the credentials from the URL once stored, so they don't linger
  params.delete('token');
  params.delete('refreshToken');
  params.delete('pricingId');
  const remaining = params.toString();
  const cleanedUrl = `${window.location.pathname}${
    remaining ? `?${remaining}` : ''
  }${window.location.hash}`;
  window.history.replaceState(window.history.state, '', cleanedUrl);

  if (refreshToken) {
    startRefreshTokenProcess(refreshToken, {
      newToken: (newToken: string, newRefreshToken: string) => {
        Session.set(OPAQUE_TOKEN, newToken);
        Session.set(REFRESH_TOKEN, newRefreshToken);
      },
    });
  }
}

// StandAlone flow logic
if (
  typeof window !== 'undefined' &&
  window.self === window.top &&
  !Session.get(USER_TYPE)
) {
  Session.set(USER_TYPE, { userType: 'StandAlone' });
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <Provider store={configureStore()}>
    <ThemeProvider theme={getDefaultTheme()}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ThemeProvider>
  </Provider>,
);
